import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import {RINGBA_DNI_PHONE} from "@/lib/constants";
export const meta = {
  title: "How to File a Home Insurance Claim",
  description: "Whether it's your first time filing a home insurance claim or you need a refresher, this in-depth guide walks you step-by-step through the entire process.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/AdobeStock_555123615-scaled.webp",
    alt: "How to File a Home Insurance Claim"
  },
  author: {
    name: "Rebecca Henderson, Kara Credle",
    role: "Author",
    image: ""
  },
  publishedAt: "July 15, 2025",
  updatedAt: "July 15, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    img: "img",
    li: "li",
    p: "p",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: ["Each year, homeowners nationwide file ", _jsx(_components.a, {
        href: "/insurance/home",
        children: "home insurance"
      }), " claims for everything from roof damage caused by hailstorms to the destructive aftermath of hurricanes. For many homeowners, filing a claim may be their first time navigating the complex and often confusing process of interacting with their home insurance provider."]
    }), "\n", _jsx(_components.p, {
      children: "To help you feel more confident the next time you file a home insurance claim, we put together a comprehensive guide, complete with tips on when you should file a claim and when it’s better to cover repair costs yourself. Discover common mistakes you can avoid when submitting a home insurance claim, and how different types of claims can affect your annual premium. We also consulted industry experts for the best advice around navigating the claims process."
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "Key Takeaways"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Wind and hail damage are the most common home insurance claim, with one in 35 insured homes filing this type of claim each year."
      }), "\n", _jsx(_components.li, {
        children: "Filing a single home insurance claim can increase your premium anywhere from 17% to 29% depending on the type of damage, with weather claims causing a 17% increase while fire claims result in a 29% premium hike."
      }), "\n", _jsx(_components.li, {
        children: "Successful home insurance claims begin with proper preparation, which includes understanding your policy coverage, maintaining detailed documentation of your home and belongings, and keeping insurer contact information readily accessible before any damage occurs."
      }), "\n", _jsx(_components.li, {
        children: "One of the most common mistakes homeowners make is failing to document damage immediately after it occurs, which can significantly impact the success of their claim."
      }), "\n", _jsx(_components.li, {
        children: "If the claim’s potential cost is less than your deductible, it’s better to pay out of pocket rather than risk premium increases and losing claim-free discounts that could save more money in the long run."
      }), "\n", _jsx(_components.li, {
        children: "Repairing or rebuilding your home after a major disaster takes at least 18 to 24 months."
      }), "\n"]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "When to File a Claim"
    }), "\n", _jsxs(_components.p, {
      children: ["It’s best to file a claim immediately after damage has occurred, as there’s typically a limited window in which to do so, according to the ", _jsx(_components.a, {
        href: "https://content.naic.org/article/what-you-need-know-when-filing-homeowners-claim",
        children: "National Association of Insurance Commissioners"
      }), " (NAIC). Time limits to submit a home insurance claim vary anywhere from ", _jsx(_components.a, {
        href: "https://baldwin.com/insights/knowing-your-home-insurance-claim-time-limit/",
        children: "30 days"
      }), " to three years, but specifics will vary by state, insurance company, and your specific policy. It’s also a good rule of thumb to assume that filing a home insurance claim will raise your rates, but by how much depends on the following, according to Insure.com:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Claim type"
      }), "\n", _jsx(_components.li, {
        children: "Settlement amount"
      }), "\n", _jsx(_components.li, {
        children: "Frequency of claims"
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["It is important to consider that premium could go up after filing a claim. With this in mind, homeowners should carefully consider when to file a claim—ideally, well before any covered incidents occur—so they can respond accordingly. “Filing a claim will mean higher premiums in the future, and the deductible will have to be met. So, minor issues don’t need to be claimed,” says Melanie Musson, insurance and finance expert at ", _jsx(_components.a, {
        href: "http://clearsurance.com",
        children: "Clearsurance.com"
      }), ". If the claim’s cost totals less than your deductible, it’s better to avoid filing a claim and pay the difference out of pocket than risk a rate increase."]
    }), "\n", _jsxs(_components.h3, {
      children: ["Step-by-Step Guide to Filing a Claim", _jsx(_components.img, {
        src: "/images/blog/AdobeStock_267253482-1024x683.webp",
        alt: "Homeowner following the step-by-step guide to filing a home insurance claim"
      })]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Credit: Adobe – Royalty Free"
      })
    }), "\n", _jsx(_components.p, {
      children: "The home insurance claims process begins when homeowners contact their insurance company to report a loss. After evaluating the claim, insurers engage in negotiations before settling with homeowners, who have the option to appeal the settlement if they feel it’s unsatisfactory. Even if you’ve filed a home insurance claim before, the step-by-step guide below can help you better understand the process to make more informed decisions regarding future claims."
    }), "\n", _jsx(_components.h4, {
      children: "Before a Claim: Prepare Ahead of Time"
    }), "\n", _jsxs(_components.p, {
      children: ["The best preparation for filing a claim begins with understanding what’s covered in your home insurance policy and what’s excluded. “It’s important to understand the policy terms. If something isn’t covered, the claim will be denied,” says Melanie Musson, insurance and finance expert at ", _jsx(_components.a, {
        href: "http://clearsurance.com",
        children: "Clearsurance.com"
      }), ". You can save yourself a lot of time and resources—and limit the potential of a premium increase—by only filing claims on damage covered by your policy."]
    }), "\n", _jsx(_components.p, {
      children: "Below are key strategies you can use to prepare for a home insurance claim should you need to file one:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Review your homeowners insurance policy"
        }), " well before ", _jsx(_components.a, {
          href: "/home-insurance/hurricane-season-insurance-guide",
          children: "hurricane season"
        }), " starts to ensure you have appropriate coverage, including any named storm or hurricane deductibles you may be responsible for before."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Maintain strong documentation"
        }), " of your home and personal belongings, even if you don’t live in an area frequently hit by hurricanes."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Inventory your possessions"
        }), " by writing down categorized lists and taking pictures to officially document what you own, according to the ", _jsx(_components.a, {
          href: "https://content.naic.org/article/what-you-need-know-when-filing-homeowners-claim",
          children: "NAIC"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Keep track of your insurance company’s contact information"
        }), ", including the agent’s name and phone number, your policy and coverage limits, and a list of trusted professionals, such as contractors, roofers, and adjusters."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Create a claims folder"
        }), " you can consult and add to when you need to make and track a claim."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["“I have advised quite a lot of clients to keep a detailed paper trail from the first day of contact with their insurance agent all the way to the final transaction,” says Nathan Mathews, Founder and CEO of ", _jsx(_components.a, {
        href: "http://roofer.com",
        children: "Roofer.com"
      }), "."]
    }), "\n", _jsx(_components.h4, {
      children: "During a Claim: How to File an Insurance Claim the Right Way"
    }), "\n", _jsxs(_components.p, {
      children: ["As you notify your home insurance company of a sustained loss, start documenting the damage with photos and videos, according to the ", _jsx(_components.a, {
        href: "https://content.naic.org/article/what-you-need-know-when-filing-homeowners-claim",
        children: "NAIC"
      }), ". Keep all communication in writing and make copies of any relevant documents, from confirmation of your claim submission to emails containing your claim number and the name of your adjuster. If you speak to anyone over the phone or in person, ask for their name and contact information."]
    }), "\n", _jsxs(_components.p, {
      children: ["While insurance companies employ their own adjusters to oversee the claims process, you can also hire a public adjuster to help guide you through the process, according to ", _jsx(_components.a, {
        href: "https://www.adjustersinternational.com/resources/news-and-events/how-to-file-a-homeowners-property-damage-insurance-claim/",
        children: "Adjusters International"
      }), ". It’s also important to know that you don’t have to use the insurance company’s vendors, and that you can report more damage after the insurance adjuster makes their initial report. Maintain close contact with adjusters to stay informed about any developments with your claim, advises Mathews. “Oftentimes, homeowners only rely on what they’re told on the phone and do not follow-up any statuses of their negotiations.”"]
    }), "\n", _jsxs(_components.p, {
      children: ["“If something needs to be replaced, a good case should be made that repairs will not be sufficient,” says Musson. Retaining experts like engineers, architects, and contractors can help you make your case for replacement if applicable, according to the ", _jsx(_components.a, {
        href: "https://www.napia.com/news/tips-to-maximize-your-claim-after-a-hurricane",
        children: "National Association of Public Insurance Adjusters"
      }), ". “Receipts for contractors, additional living expenses, and any other relevant expenses are important to support the costs related to the claims,” says Musson."]
    }), "\n", _jsx(_components.h4, {
      children: "After a Claim: What to Expect and How to Follow Through"
    }), "\n", _jsx(_components.p, {
      children: "After a major disaster, homeowners can expect the following to occur:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Replacing your personal possessions and repairing or rebuilding your home typically takes at least 18 to 24 months, according to the ", _jsx(_components.a, {
          href: "https://content.naic.org/article/what-you-should-know-about-settling-homeowners-insurance-claim",
          children: "NAIC"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["Settlement offers usually come in separate parts, such as payments for Additional Living Expenses, personal property damage, and damage to a home’s structure, according to the ", _jsx(_components.a, {
          href: "https://content.naic.org/article/what-you-should-know-about-settling-homeowners-insurance-claim",
          children: "NAIC"
        }), "."]
      }), "\n", _jsx(_components.li, {
        children: "Any settlement checks will also include your lender, so you’ll need to coordinate obtaining their signature to release the corresponding funds."
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["If the settlement isn’t satisfactory, request any denials in writing. “Insurance companies are businesses,” says Musson. “They’re going to err on the side of protecting their bottom line when there’s a gray area. In such a situation, the homeowner needs to appeal and escalate the claim to third-party adjusters and appraisers.” Consider obtaining an ", _jsx(_components.a, {
        href: "https://www.ncdoi.gov/consumers/disaster/after-storm/claims-and-adjuster-information",
        children: "appraisal"
      }), " for an unbiased evaluation of value, or contacting your state insurance department to determine available methods of recourse."]
    }), "\n", _jsx(_components.h3, {
      children: "How Filing a Claim Can Impact Home Insurance Premiums"
    }), "\n", _jsx(_components.p, {
      children: "The chart below describes the average premium increase after filing one, then two claims, according to Insure.com with Quadrant Information Services. Filing a single claim can increase your premium by anywhere from 17% to 29%, with weather-related claims causing the lowest increase (17%) and fire claims the highest (29%). Explore more of the relationship between claim-filing frequency and premium increase below:"
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Type of Home Insurance Claim"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Average Premium Increase After First Filing"
            })
          }), _jsx(_components.th, {
            children: _jsx(_components.strong, {
              children: "Average Premium Increase After Second Filing"
            })
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Fire"
          }), _jsx(_components.td, {
            children: "29%"
          }), _jsx(_components.td, {
            children: "60%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Theft"
          }), _jsx(_components.td, {
            children: "27%"
          }), _jsx(_components.td, {
            children: "55%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Liability"
          }), _jsx(_components.td, {
            children: "25%"
          }), _jsx(_components.td, {
            children: "52%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Water"
          }), _jsx(_components.td, {
            children: "25%"
          }), _jsx(_components.td, {
            children: "50%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Medical"
          }), _jsx(_components.td, {
            children: "18%"
          }), _jsx(_components.td, {
            children: "24%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Weather"
          }), _jsx(_components.td, {
            children: "17%"
          }), _jsx(_components.td, {
            children: "29%"
          })]
        })]
      })]
    }), "\n", _jsx(_components.h4, {
      children: "What Else Could Cause a Home Insurance Premium to Increase?"
    }), "\n", _jsx(_components.p, {
      children: "Homeowners nationwide pay an average annual premium of $2,927 for $350,000 in dwelling coverage and a $1,000 deductible. While claims can increase that annual premium, they’re not the only driver. Additional factors besides claims that could result in an insurance premium increase include the following:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Increased frequency of home insurance claims in your area"
      }), "\n", _jsx(_components.li, {
        children: "Home upkeep and distinctive features"
      }), "\n", _jsx(_components.li, {
        children: "Certain pet breeds and types"
      }), "\n", _jsx(_components.li, {
        children: "Home age and construction"
      }), "\n", _jsx(_components.li, {
        children: "Your credit history"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "When assessing risk, insurance carriers often reference a property’s Comprehensive Loss Underwriting Exchange (CLUE) report for the last five to seven years. A CLUE report includes a record of insurance claims history for a particular property, not a homeowner. However, if you’ve lived in your home for less than five years, the insurer will likely use claims data for the property based on claims made by one or more previous homeowners."
    }), "\n", _jsx(_components.h3, {
      children: "Common Mistakes to Avoid"
    }), "\n", _jsx(_components.p, {
      children: "Failing to document damage immediately after it occurs directly impacts the success of a homeowner’s insurance claim. “Homeowners often wait for the last minute to take action when documenting, and by the time they actually document it, the damage is no longer evident, lost or deteriorated,” says Mathews."
    }), "\n", _jsx(_components.p, {
      children: "The urge to clean up or repair damage after a loss can prove strong, but without supporting evidence of the actual loss, claims and their corresponding settlements can fall short of the incurred loss. “Documenting damage and value is important to maximize settlements,” says Musson."
    }), "\n", _jsx(_components.p, {
      children: "A lack of knowledge regarding specific insurance policy coverage can also put homeowners at risk of having to pay for repairs or rebuilding out of pocket. “Homeowners should review their policy. Once they understand what’s covered, they can minimize issues,” says Musson. Clarifying coverage particulars beforehand can also help homeowners identify—and fill—any gaps."
    }), "\n", _jsx(_components.h3, {
      children: "How Home Insurance Claims Differ by Type"
    }), "\n", _jsxs(_components.p, {
      children: ["Every year, damage from high winds, hail, fire, lightning, water, and freezing temperatures prompts homeowners to file insurance claims, in addition to those due to theft and vandalism. The frequency and extent of damage vary greatly among ", _jsx(_components.a, {
        href: "/home-insurance/home-insurance-statistics",
        children: "home insurance"
      }), " claims. Explore more differences between these common home insurance claims below."]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/images/blog/AdobeStock_379428893-1024x683.webp",
        alt: "Home damaged by wind and hail, a common insurance claim type"
      })
    }), "\n", _jsx(_components.p, {
      children: "Credit: Adobe – Royalty Free"
    }), "\n", _jsx(_components.h4, {
      children: "Wind and Hail"
    }), "\n", _jsxs(_components.p, {
      children: ["One in 35 insured homes files a claim due to wind and hail every year, representing the most frequent type of home insurance claim, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
        children: "Insurance Information Institute"
      }), ". In the U.S., hailstorms are most common from May through August, according to ", _jsx(_components.a, {
        href: "https://www.insuranceclaimrecoverysupport.com/loss-types/",
        children: "Insurance Claim Recovery Support"
      }), " (ICRS). High winds and large hail can significantly damage a home’s structure, ripping away shingles and siding or breaking windows and puncturing through the roof, exposing the interior to further damage."]
    }), "\n", _jsxs(_components.p, {
      children: ["The national average premium for a home with a history of a wind and hail claim is $2,884, just $43 less than the national average. Between 2018 and 2022, wind and hail accounted for an average of 42% of insurance losses, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
        children: "Insurance Information Institute"
      }), ". During those five years, 2.8% of insured homes had a loss due to wind and hail, with the average claim severity totaling $13,511 per claim."]
    }), "\n", _jsx(_components.h4, {
      children: "Fire and Lightning"
    }), "\n", _jsxs(_components.p, {
      children: ["Annually, one in every 425 insured homes files a claim due to fire and lightning, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
        children: "Insurance Information Institute"
      }), ". Properties with a history of filing a claim due to fire and lightning correspond to a national average premium of $3,144, a $217 increase from the national average."]
    }), "\n", _jsx(_components.p, {
      children: "From 2018 to 2022, fire and lightning accounted for an average of 22.6% of insurance losses. Additionally, lightning and fire had an average claim severity of $83,991, the highest among all types of insurance losses. Fire—whether started on the ground or sparked by lightning—can quickly destroy personal possessions and erode a home’s structure, leaving behind smoke and damage from the water used to extinguish the flames."
    }), "\n", _jsx(_components.h4, {
      children: "Water Damage and Freezing"
    }), "\n", _jsxs(_components.p, {
      children: ["Accounting for nearly one-fourth (24.7%) of insurance losses on average from 2018 to 2022, water damage and freezing cause one in 60 insured homes to file a claim annually, per the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
        children: "Insurance Information Institute"
      }), ". During that same period, 1.6% of insured homes experienced a loss due to water damage and freezing, with the average claim amounting to $13,954."]
    }), "\n", _jsx(_components.p, {
      children: "Cold temperatures can cause water pipes to freeze and burst, but even small leaks can cause significant water damage. Homeowners of a property with a history of water damage and freezing claims pay an average of $3,106 annually, or $179 more than the national average."
    }), "\n", _jsx(_components.h4, {
      children: "Theft"
    }), "\n", _jsxs(_components.p, {
      children: ["Only 0.14% of insured homes experienced a loss due to theft over the five years from 2018 to 2022, resulting in a $5,024 average claim severity, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
        children: "Insurance Information Institute"
      }), ". Every year, one in 700 homes has a claim due to theft. This type of insurance claim also accounted for an average of 1% of insurance losses between 2018 and 2022."]
    }), "\n", _jsx(_components.h4, {
      children: "Other"
    }), "\n", _jsxs(_components.p, {
      children: ["All other property damage—including malicious mischief and vandalism—comprised an average of 7.6% of insurance losses from 2018 to 2022, per the ", _jsx(_components.a, {
        href: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
        children: "Insurance Information Institute"
      }), ". With claim severity averaging $7,798, only 0.9% of insured homes experienced a loss due to vandalism or malicious mischief during the same five-year span."]
    }), "\n", _jsx(_components.h3, {
      children: "Conclusion"
    }), "\n", _jsxs(_components.p, {
      children: ["Now that you understand more about the claims process, review your home insurance coverage to determine if your policy covers ", _jsx(_components.a, {
        href: "/home-insurance/hurricane-damage-statistics",
        children: "hurricane damage"
      }), ", a ", _jsx(_components.a, {
        href: "/home-insurance/house-fire-statistics",
        children: "house fire"
      }), ", or ", _jsx(_components.a, {
        href: "/home-insurance/water-damage-statistics",
        children: "water damage"
      }), " to gain peace of mind should any of these occur. If you live in a ", _jsx(_components.a, {
        href: "/home-insurance/least-resilient-coastal-cities",
        children: "coastal city"
      }), ", consider adding ", _jsx(_components.a, {
        href: "/home-insurance/flood-insurance",
        children: "flood insurance"
      }), " to your policy—if you’re not already required to carry this often-overlooked-but-vital coverage. Want to make sure you’re getting the best coverage for what you pay? ", _jsx("a", {
        href: `tel:${RINGBA_DNI_PHONE}`,
        children: "Chat"
      }), " with one of our zero-commission agents to save an average of $895 per year."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
